import { isDecimalAmount } from '@platform/design-system';
import { z } from 'zod';
import {
  CARRIER_SECRET_MAX_LENGTH,
  CARRIER_SETTING_MAX_LENGTH,
  type Carrier,
  type CarrierAccount,
  type CarrierAccountBody,
} from './carriers.ts';

const requiredText = (maxLength: number) => z.string().trim().min(1).max(maxLength);

const fieldsSchema = (fields: readonly string[], maxLength: number) =>
  z.object(Object.fromEntries(fields.map((field) => [field, requiredText(maxLength)])));

export const carrierAccountFormSchema = (carrier: Pick<Carrier, 'credentialFields' | 'settingFields'>) =>
  z.object({
    enabled: z.boolean(),
    credentials: fieldsSchema(carrier.credentialFields, CARRIER_SECRET_MAX_LENGTH),
    settings: fieldsSchema(carrier.settingFields, CARRIER_SETTING_MAX_LENGTH),
    customerFee: z
      .string()
      .trim()
      .refine((fee) => fee === '' || isDecimalAmount(fee)),
  });

export type CarrierAccountFormValues = {
  readonly enabled: boolean;
  readonly credentials: Readonly<Record<string, string>>;
  readonly settings: Readonly<Record<string, string>>;
  readonly customerFee: string;
};

export const accountFormOf = (
  carrier: Pick<Carrier, 'credentialFields' | 'settingFields'>,
  existing: CarrierAccount | undefined,
): CarrierAccountFormValues => ({
  enabled: existing?.enabled ?? true,
  credentials: Object.fromEntries(carrier.credentialFields.map((field) => [field, ''])),
  settings: Object.fromEntries(
    carrier.settingFields.map((field) => [field, existing?.settings[field] ?? '']),
  ),
  customerFee: existing?.customerFee?.amount ?? '',
});

const trimmed = (values: Readonly<Record<string, string>>): Record<string, string> =>
  Object.fromEntries(Object.entries(values).map(([field, value]) => [field, value.trim()]));

export const accountBodyOf = (values: CarrierAccountFormValues, currency: string): CarrierAccountBody => ({
  subsidiaryId: null,
  enabled: values.enabled,
  credentials: trimmed(values.credentials),
  settings: trimmed(values.settings),
  customerFee: values.customerFee.trim() === '' ? null : { amount: values.customerFee.trim(), currency },
});
